import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";

interface SettingRowProps {
  label: ReactNode;
  /** One line under the label: the current state or what the setting does. */
  description?: ReactNode;
  /** An icon or logo left of the label. */
  icon?: ReactNode;
  /** Right-aligned: the control, a status, or the button that changes it. */
  action?: ReactNode;
}

/**
 * One setting in a settings panel: what it is and its state on the left, what
 * to do about it on the right. Stack them in a `Panel disablePadding`; rows
 * after the first draw a divider above themselves.
 */
export default function SettingRow({ label, description, icon, action }: SettingRowProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1.75,
        flexWrap: { xs: "wrap", sm: "nowrap" },
        "& + &": { borderTop: 1, borderColor: "divider" },
      }}
    >
      {icon != null && (
        <Box sx={{ display: "flex", color: "text.secondary", flexShrink: 0, "& svg": { fontSize: 20 } }}>
          {icon}
        </Box>
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        {description != null && (
          <Typography variant="body2" component="div" sx={{ color: "text.secondary" }}>
            {description}
          </Typography>
        )}
      </Box>
      {action != null && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>{action}</Box>
      )}
    </Box>
  );
}
